import { useMemo, useState } from 'react'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { Button } from '@/shared/components/ui/button'
import { Card } from '@/shared/components/ui/card'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { AlertCircle, Download, Database, RefreshCw, UploadCloud } from 'lucide-react'
import {
  ContactoFormDialog,
  ContactosFilters,
  ContactosTable,
  StatsCards,
  UploadDialog,
} from '../components'
import {
  useBuscarContactos,
  useContactos,
  useDeleteContacto,
  useEspecialidades,
  useExportarCsv,
  useStats,
  useUpdateContacto,
} from '@/modules/bd-unificada/hooks/useContactos'
import { useCanAdmin } from '@/modules/bd-unificada/hooks/usePermisos'
import type { Contacto, ContactoUpdateInput } from '@/modules/bd-unificada/types/contacto'

export function ContactosPage() {
  useDocumentTitle('BD Unificada — Contactos')
  const canAdmin = useCanAdmin()

  const [pagina, setPagina] = useState(1)
  const porPagina = 25
  const [search, setSearch] = useState('')
  const [tipo, setTipo] = useState<string | undefined>()
  const [pais, setPais] = useState<string | undefined>()
  const [especialidad, setEspecialidad] = useState<string | undefined>()
  const [ciudad, setCiudad] = useState<string | undefined>()
  const [provincia, setProvincia] = useState<string | undefined>()

  const [contactoSel, setContactoSel] = useState<Contacto | null>(null)
  const [editOpen, setEditOpen] = useState(false)
  const [uploadOpen, setUploadOpen] = useState(false)

  const buscando = search.trim().length >= 2

  const { data: listado, isLoading, refetch } = useContactos({
    pagina,
    por_pagina: porPagina,
    tipo,
    pais,
    especialidad,
    ciudad,
    provincia,
  })
  const { data: resultadosBusqueda, isLoading: cargandoBusqueda } = useBuscarContactos(search)
  const { data: stats, isLoading: cargandoStats } = useStats()
  const { data: especialidades } = useEspecialidades()

  const updateContacto = useUpdateContacto()
  const deleteContacto = useDeleteContacto()
  const exportar = useExportarCsv()

  const contactos = useMemo<Contacto[]>(() => {
    if (buscando) return resultadosBusqueda ?? []
    return listado?.items ?? []
  }, [buscando, resultadosBusqueda, listado])

  const totalPaginas = listado ? Math.max(1, Math.ceil(listado.total / listado.por_pagina)) : 1
  const cargando = buscando ? cargandoBusqueda : isLoading

  const handleEdit = (c: Contacto) => {
    setContactoSel(c)
    setEditOpen(true)
  }

  const handleDelete = (c: Contacto) => {
    if (confirm(`¿Eliminar el contacto "${c.nombre_completo}"?`)) {
      deleteContacto.mutate(c.id)
    }
  }

  const handleSubmitEdit = async (data: ContactoUpdateInput) => {
    if (!contactoSel) return
    await updateContacto.mutateAsync({ id: contactoSel.id, data })
    setEditOpen(false)
    setContactoSel(null)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Database className="h-6 w-6 text-blue-500" />
          BD Unificada — Contactos
        </h1>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => refetch()} disabled={cargando}>
            <RefreshCw className={`h-4 w-4 ${cargando ? 'animate-spin' : ''}`} />
          </Button>
          {canAdmin && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => exportar.mutate({ tipo, pais, especialidad, ciudad, provincia })}
                disabled={exportar.isPending}
              >
                <Download className="mr-2 h-4 w-4" /> Exportar CSV
              </Button>
              <Button
                size="sm"
                onClick={() => setUploadOpen(true)}
                className="bg-blue-800 hover:bg-blue-900 text-white"
              >
                <UploadCloud className="mr-2 h-4 w-4" /> Nueva carga
              </Button>
            </>
          )}
        </div>
      </div>

      <StatsCards stats={stats} isLoading={cargandoStats} />

      <ContactosFilters
        search={search}
        onSearchChange={(v) => {
          setSearch(v)
          setPagina(1)
        }}
        tipo={tipo}
        onTipoChange={(v) => {
          setTipo(v)
          setPagina(1)
        }}
        pais={pais}
        onPaisChange={(v) => {
          setPais(v)
          setPagina(1)
        }}
        especialidad={especialidad}
        onEspecialidadChange={(v) => {
          setEspecialidad(v)
          setPagina(1)
        }}
        especialidades={especialidades}
        ciudad={ciudad}
        onCiudadChange={(v) => {
          setCiudad(v)
          setPagina(1)
        }}
        provincia={provincia}
        onProvinciaChange={(v) => {
          setProvincia(v)
          setPagina(1)
        }}
      />

      <Card className="border border-gray-300 dark:border-gray-700 p-6">
        {cargando ? (
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : contactos.length > 0 ? (
          <>
            <ContactosTable
              contactos={contactos}
              canEdit={canAdmin}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />

            {!buscando && listado && totalPaginas > 1 && (
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-muted-foreground">
                  Mostrando {(pagina - 1) * porPagina + 1} –{' '}
                  {Math.min(pagina * porPagina, listado.total)} de {listado.total} contactos
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPagina((p) => p - 1)}
                    disabled={pagina === 1}
                  >
                    Anterior
                  </Button>
                  <div className="text-sm">Página {pagina} de {totalPaginas}</div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPagina((p) => p + 1)}
                    disabled={pagina >= totalPaginas}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
            {buscando && (
              <p className="mt-4 text-sm text-muted-foreground">
                Resultados de búsqueda (máx. 100). Limpia el buscador para paginar el listado
                completo.
              </p>
            )}
          </>
        ) : (
          <div className="p-12 text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-muted-foreground/50" />
            <h3 className="mt-4 text-lg font-semibold">No se encontraron contactos</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Ajusta los filtros o realiza una nueva carga de datos.
            </p>
          </div>
        )}
      </Card>

      <ContactoFormDialog
        open={editOpen}
        onOpenChange={(o) => {
          setEditOpen(o)
          if (!o) setContactoSel(null)
        }}
        onSubmit={handleSubmitEdit}
        isSubmitting={updateContacto.isPending}
        contacto={contactoSel}
      />

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} />
    </div>
  )
}
